import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { stripeConnectService } from '../../../services/stripeConnectService';
import receiptService from '../../../services/receiptService';

const WithdrawalHistory = ({ history, availableBalance }) => {
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [eligiblePayouts, setEligiblePayouts] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPayouts();
  }, []);

  const loadPayouts = async () => {
    try {
      const [eligible, pending] = await Promise.all([
        stripeConnectService?.getEligiblePayouts(),
        stripeConnectService?.getPendingPayouts()
      ]);
      setEligiblePayouts(eligible || []);
      setPendingPayouts(pending || []);
    } catch (err) {
      console.error('Failed to load payouts:', err);
      setError(err?.message || 'Failed to load payout data');
    }
  };

  const handleSelectPayout = (payoutId) => {
    setSelectedPayouts(prev => {
      if (prev?.includes(payoutId)) {
        return prev?.filter(id => id !== payoutId);
      }
      return [...prev, payoutId];
    });
  };

  const calculateSelectedAmount = () => {
    return eligiblePayouts
      ?.filter(p => selectedPayouts?.includes(p?.id))
      ?.reduce((sum, p) => sum + parseFloat(p?.netAmount || 0), 0);
  };

  const handleWithdrawal = async () => {
    if (selectedPayouts?.length === 0) {
      setError('Please select at least one payout to withdraw from the "Available for Withdrawal" list below.');
      setShowWithdrawalModal(false);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Step 1: Request all selected payouts (validates eligibility & sets status to 'requested')
      const requestResults = await Promise.allSettled(
        selectedPayouts?.map(payoutId => stripeConnectService?.requestPayout(payoutId))
      );

      const failedRequests = requestResults?.filter(r => r?.status === 'rejected');
      if (failedRequests?.length > 0) {
        const firstError = failedRequests?.[0];
        throw new Error(
          firstError?.status === 'rejected' ? firstError?.reason?.message || 'Failed to request payout' : 'Failed to request payout'
        );
      }

      // Step 2: Process (trigger Stripe transfer) for successfully requested payouts
      const successfulIds = requestResults
        ?.map((r, i) => r?.status === 'fulfilled' ? selectedPayouts?.[i] : null)
        ?.filter(Boolean);

      const processResults = await Promise.allSettled(
        successfulIds?.map(payoutId => stripeConnectService?.processPayout(payoutId))
      );

      const failedProcessing = processResults?.filter(r => r?.status === 'rejected');
      if (failedProcessing?.length > 0) {
        const firstError = failedProcessing?.[0];
        throw new Error(
          firstError?.status === 'rejected' ? firstError?.reason?.message || 'Failed to process payout' : 'Failed to process payout'
        );
      }

      setShowWithdrawalModal(false);
      setSelectedPayouts([]);
      setWithdrawalAmount('');

      // Reload data
      await loadPayouts();
      window.location?.reload();
    } catch (err) {
      console.error('Withdrawal error:', err);
      setError(err?.message || 'Failed to process withdrawal request');
      setShowWithdrawalModal(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadReceipt = async (payout) => {
    try {
      const receiptData = {
        id: payout?.id,
        stripeTransferId: payout?.stripeTransferId,
        grossAmount: payout?.grossAmount,
        platformFeeAmount: payout?.platformFeeAmount,
        netAmount: payout?.netAmount,
        payoutDate: payout?.payoutDate,
        ownerName: payout?.bookings?.userProfiles?.fullName || 'N/A',
        ownerEmail: payout?.bookings?.userProfiles?.email || 'N/A',
        trailerTitle: payout?.bookings?.trailerListings?.title || 'N/A',
        startDate: payout?.bookings?.startDate,
        endDate: payout?.bookings?.endDate,
        bookingReference: payout?.bookings?.bookingReference,
        accountLast4: '****'
      };
      
      receiptService?.generateTransactionReceipt(receiptData);
    } catch (err) {
      console.error('Failed to generate receipt:', err);
      setError('Failed to download receipt. Please try again.');
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { label: 'Completed', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      case 'processing':
        return { label: 'Processing', color: 'bg-warning/10 text-warning', icon: 'Clock' };
      case 'failed':
        return { label: 'Failed', color: 'bg-error/10 text-error', icon: 'XCircle' };
      default:
        return { label: 'Unknown', color: 'bg-muted text-muted-foreground', icon: 'AlertCircle' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTimeRemaining = (eligibleDate) => {
    const now = new Date();
    const eligible = new Date(eligibleDate);
    const diffMs = eligible - now;
    
    if (diffMs <= 0) return 'Available now';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }
    return `${hours}h ${minutes}m remaining`;
  };

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-body text-sm text-error">{error}</p>
          </div>
        </div>
      )}

      {/* Withdrawal Request Card */}
      <div className="bg-gradient-to-br from-primary to-primary/80 rounded-lg p-6 text-primary-foreground">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold mb-2">Available Balance</h2>
            <p className="font-heading text-4xl font-bold mb-2">${availableBalance?.toLocaleString()}</p>
            <p className="font-caption text-sm opacity-90">Ready to withdraw to your bank account</p>
          </div>
          <Button
            variant="secondary"
            size="lg"
            iconName="Download"
            onClick={() => setShowWithdrawalModal(true)}
            className="bg-white text-primary hover:bg-white/90"
          >
            Request Withdrawal
          </Button>
        </div>
      </div>

      {/* Eligible Payouts Section */}
      {eligiblePayouts?.length > 0 && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
              <Icon name="CheckCircle" size={24} strokeWidth={2} className="text-success" />
              <span>Available for Withdrawal</span>
            </h2>
            <span className="px-3 py-1 bg-success/10 text-success text-sm font-medium rounded-full">
              {eligiblePayouts?.length} {eligiblePayouts?.length === 1 ? 'payout' : 'payouts'}
            </span>
          </div>

          <div className="space-y-3">
            {eligiblePayouts?.map((payout) => (
              <div key={payout?.id} className="border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-smooth">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedPayouts?.includes(payout?.id)}
                    onChange={() => handleSelectPayout(payout?.id)}
                    className="w-5 h-5 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-body font-semibold text-foreground">
                        {payout?.bookings?.trailerListings?.title || 'Rental Payout'}
                      </h3>
                      <p className="font-heading text-xl font-bold text-success">
                        ${parseFloat(payout?.netAmount || 0)?.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span className="flex items-center space-x-1">
                        <Icon name="Calendar" size={14} strokeWidth={2} />
                        <span>Rental: {formatDate(payout?.bookings?.startDate)} - {formatDate(payout?.bookings?.endDate)}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="DollarSign" size={14} strokeWidth={2} />
                        <span>Gross: ${parseFloat(payout?.grossAmount || 0)?.toFixed(2)}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="Percent" size={14} strokeWidth={2} />
                        <span>Fee: ${parseFloat(payout?.platformFeeAmount || 0)?.toFixed(2)} (15%)</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedPayouts?.length > 0 && (
            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-body text-sm text-muted-foreground mb-1">
                    {selectedPayouts?.length} {selectedPayouts?.length === 1 ? 'payout' : 'payouts'} selected
                  </p>
                  <p className="font-heading text-2xl font-bold text-foreground">
                    ${calculateSelectedAmount()?.toFixed(2)}
                  </p>
                </div>
                <Button
                  variant="default"
                  size="lg"
                  iconName="Download"
                  onClick={handleWithdrawal}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Request Withdrawal'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending Payouts Section */}
      {pendingPayouts?.length > 0 && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Clock" size={24} strokeWidth={2} className="text-warning" />
              <span>Pending Eligibility (48-Hour Hold)</span>
            </h2>
            <span className="px-3 py-1 bg-warning/10 text-warning text-sm font-medium rounded-full">
              {pendingPayouts?.length} {pendingPayouts?.length === 1 ? 'payout' : 'payouts'}
            </span>
          </div>

          <div className="space-y-3">
            {pendingPayouts?.map((payout) => (
              <div key={payout?.id} className="border border-border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-body font-semibold text-foreground">
                    {payout?.bookings?.trailerListings?.title || 'Rental Payout'}
                  </h3>
                  <p className="font-heading text-xl font-bold text-foreground">
                    ${parseFloat(payout?.netAmount || 0)?.toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} strokeWidth={2} />
                      <span>Rental ended: {formatDate(payout?.bookings?.endDate)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="DollarSign" size={14} strokeWidth={2} />
                      <span>Gross: ${parseFloat(payout?.grossAmount || 0)?.toFixed(2)}</span>
                    </span>
                  </div>
                  <span className="flex items-center space-x-1 text-warning font-medium">
                    <Icon name="Clock" size={14} strokeWidth={2} />
                    <span>{formatTimeRemaining(payout?.eligibleForPayoutAt)}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-accent/10 border border-accent/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={18} className="text-accent flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="font-body text-sm text-muted-foreground">
                Payouts become available 48 hours after the rental end date. This waiting period protects both owners and renters by allowing time for return inspection and dispute resolution.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal History */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
            <Icon name="History" size={24} strokeWidth={2} className="text-primary" />
            <span>Withdrawal History</span>
          </h2>
        </div>

        {history?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-body text-muted-foreground">No withdrawal history yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-body font-semibold text-sm text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 font-body font-semibold text-sm text-muted-foreground">Amount</th>
                  <th className="text-left py-3 px-4 font-body font-semibold text-sm text-muted-foreground">Transaction ID</th>
                  <th className="text-left py-3 px-4 font-body font-semibold text-sm text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 font-body font-semibold text-sm text-muted-foreground">Method</th>
                  <th className="text-right py-3 px-4 font-body font-semibold text-sm text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history?.map((transaction) => {
                  const statusConfig = getStatusConfig(transaction?.status);
                  return (
                    <tr key={transaction?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                      <td className="py-4 px-4 font-body text-sm text-foreground">
                        {formatDate(transaction?.requestDate)}
                      </td>
                      <td className="py-4 px-4 font-heading font-semibold text-foreground">
                        ${transaction?.amount?.toFixed(2)}
                      </td>
                      <td className="py-4 px-4 font-caption text-sm text-muted-foreground">
                        {transaction?.transactionId}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig?.color}`}>
                          <Icon name={statusConfig?.icon} size={14} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4 font-body text-sm text-muted-foreground">
                        {transaction?.method}
                      </td>
                      <td className="py-4 px-4 text-right">
                        {transaction?.status === 'completed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Download"
                            onClick={() => handleDownloadReceipt(transaction)}
                            className="text-primary hover:text-primary/80"
                          >
                            Receipt
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Processing Time Info */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Info" size={20} className="text-accent" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-body font-semibold text-foreground mb-2">Processing Times</h3>
            <ul className="space-y-2 font-body text-sm text-muted-foreground">
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-success" strokeWidth={2} />
                <span>Direct deposits typically process within 1-3 business days</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-success" strokeWidth={2} />
                <span>Minimum withdrawal amount: $50</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-success" strokeWidth={2} />
                <span>No fees for standard withdrawals</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setShowWithdrawalModal(false)} />
          <div className="relative bg-card rounded-lg shadow-elevation-5 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-semibold text-foreground">Request Withdrawal</h3>
              <button
                onClick={() => setShowWithdrawalModal(false)}
                className="p-2 hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <div className="space-y-4">
              {selectedPayouts?.length > 0 ? (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="CheckCircle" size={18} className="text-success" strokeWidth={2} />
                    <p className="font-body text-sm font-semibold text-success">
                      {selectedPayouts?.length} payout{selectedPayouts?.length > 1 ? 's' : ''} selected
                    </p>
                  </div>
                  <p className="font-heading text-2xl font-bold text-foreground">
                    ${calculateSelectedAmount()?.toFixed(2)}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground mt-1">Total withdrawal amount</p>
                </div>
              ) : (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertCircle" size={18} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="font-body text-sm text-warning">
                      No payouts selected. Please select eligible payouts from the list below before requesting a withdrawal.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-body text-sm text-muted-foreground">Processing Time</span>
                  <span className="font-body text-sm font-medium text-foreground">1-3 business days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-muted-foreground">Withdrawal Fee</span>
                  <span className="font-body text-sm font-medium text-success">$0.00</span>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowWithdrawalModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleWithdrawal}
                  loading={isProcessing}
                  disabled={isProcessing || selectedPayouts?.length === 0}
                  className="flex-1"
                >
                  {isProcessing ? 'Processing...' : 'Confirm Withdrawal'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalHistory;